import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiError, api, fmtBytes, fmtTime, patchMission, qs, transitionMission,
  type ArchiveJob, type ArchiveNode, type Arrivals, type Calibration, type Me, type Mission as M, type StoredObject,
} from '../api'
import { invalidate, useApi } from '../useApi'
import { useT } from '../lang'
import { Link, navigate } from '../router'
import { armListen } from '../listenIntent'
import { Delivery } from '../components/Delivery'
import { PageHead } from '../components/Shell'
import { CalibrationBadge, Empty, ErrorBox, Field, Rows, Stat, StateBadge } from '../components/bits'
import { DeployMap } from '../components/DeployMap'
import { Move } from '../components/Move'
import { ListenPanel } from '../components/ListenPanel'
import { ClipPlayer } from '../components/ClipPlayer'
import { Calendar, type CalendarDay } from '../components/Calendar'
import { CardImport } from '../components/CardImport'
import { DayTimeline, type DayHour } from '../components/DayTimeline'
import { EventList, type DetectedEvent } from '../components/EventList'
import { MissionLevels } from './MissionLevels'

type Tab = 'overview' | 'levels' | 'listen' | 'recordings' | 'activity'

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  // Second, not last: "what did we hear" is the question people arrive with,
  // and it is now answerable from data the node has been sending all along.
  { id: 'levels', label: 'Sound' },
  { id: 'listen', label: 'Listen' },
  { id: 'recordings', label: 'Recordings' },
  { id: 'activity', label: 'Arrivals' },
]

export function MissionPage({ id, tab, me }: { id: string; tab: Tab; me: Me }) {
  // `tr`, not `t`: TABS.map already binds `t` to the tab.
  const tr = useT()
  const path = `/v1/missions/${encodeURIComponent(id)}`
  const q = useApi<M & { calibration: Calibration }>(path)
  const m = q.data

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!m) return null

  const title = m.site_name ? `${m.site_name}` : m.mission_id

  return (
    <>
      <PageHead
        title={title}
        lead={
          <>
            {m.compute_name ?? m.compute_id ?? 'no node'} ·{' '}
            <span className="mono">{m.mission_id}</span>
          </>
        }
        cta={
          // Hidden on the two tabs that carry their own focal action: Listen
          // owns the live player, Recordings owns the clip player. On those,
          // the accent belongs to the thing you came to press, and a second
          // one competing with it would leave neither reading as the answer.
          tab !== 'listen' && tab !== 'recordings' && (
            <button
              className="btn primary"
              onClick={() => navigate(`/missions/${encodeURIComponent(id)}/listen`)}
            >
              Listen live
            </button>
          )
        }
      />

      <div className="seg" style={{ marginBottom: 24 }} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={t.id === tab}
            onClick={() => {
              // The other door into listening, and it carries the same intent
              // as the one on the fleet: a control labelled Listen should
              // produce sound, not a panel with a second Listen on it.
              if (t.id === 'listen') armListen()
              navigate(
                t.id === 'overview'
                  ? `/missions/${encodeURIComponent(id)}`
                  : `/missions/${encodeURIComponent(id)}/${t.id}`,
              )
            }}
          >
            {tr(t.label)}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview m={m} me={me} onSaved={() => { invalidate(path); q.reload() }} />}
      {tab === 'levels' && <MissionLevels missionId={m.mission_id} me={me} />}
      {tab === 'listen' && (
        <ListenPanel
          missionId={m.mission_id}
          computeId={m.compute_id}
          calibration={m.calibration}
          canWrite={me.can_write}
        />
      )}
      {tab === 'recordings' && (
        <Recordings missionId={m.mission_id} calibration={m.calibration}
          recorderModel={m.recorder_model} recorderSerial={m.microphone_serial} />
      )}
      {tab === 'activity' && <Activity missionId={m.mission_id} me={me} />}
    </>
  )
}

/* --------------------------------------------------------------- overview */

function Overview({ m, me, onSaved }: { m: M & { calibration: Calibration }; me: Me; onSaved: () => void }) {
  const cal = m.calibration
  // Polled: a standing job's byte count is the one figure here that moves on
  // its own, and the trail should not need a refresh to be current.
  const jobs = useApi<{ items: ArchiveJob[]; node: ArchiveNode | null }>(
    `/v1/missions/${encodeURIComponent(m.mission_id)}/archive-jobs`,
    { poll: 30_000 })
  return (
    <div className="stack">
      <div className="grid c4">
        <div className="card"><Stat label="State" value={<StateBadge state={m.state} />} small /></div>
        <div className="card"><Stat label="Tier" value={m.tier ?? '—'} small /></div>
        <div className="card"><Stat label="Depth" value={m.deploy_depth_m != null ? `${m.deploy_depth_m} m` : '—'} small /></div>
        <div className="card"><Stat label="Started" value={fmtTime(m.started_at)} small /></div>
      </div>

      {m.compute_id && (
        <Delivery
          missionId={m.mission_id} jobs={jobs.data?.items ?? []}
          node={jobs.data?.node ?? null}
          canWrite={me.can_write} busy={jobs.loading} features={m.features}
          onChanged={() => { invalidate(`/v1/missions/${encodeURIComponent(m.mission_id)}/archive-jobs`); jobs.reload() }}
        />
      )}

      <div className="grid c2">
        <div className="card">
          <h2 className="display" style={{ fontSize: 17, marginBottom: 14 }}>Deployment</h2>
          <dl className="dl">
            <dt>Site</dt>
            <dd>
              {m.site_id ? (
                <Link to={`/sites/${encodeURIComponent(m.site_id)}`}>{m.site_name ?? m.site_id}</Link>
              ) : <span className="faint">unassigned</span>}
            </dd>
            {/* A recorder has no node and never will, so "—" would read as a
                binding someone forgot to make. Name the reason instead. */}
            <dt>Node</dt>
            <dd className={m.compute_id ? 'mono' : undefined}>
              {m.compute_id ?? (
                <span className="faint">
                  {m.recorder_model ? 'none — records to its own card' : '—'}
                </span>
              )}
            </dd>
            {m.recorder_model && (
              <>
                <dt>Recorder</dt>
                <dd>
                  {m.recorder_model}
                  {m.microphone_label && ` · ${m.microphone_label}`}
                </dd>
              </>
            )}
            <dt>Hydrophone</dt>
            <dd>
              {m.microphone_id ? (
                <Link to={`/calibration/${encodeURIComponent(m.microphone_id)}`}>
                  {m.microphone_serial ?? m.microphone_id}
                </Link>
              ) : <span className="faint">none bound</span>}
            </dd>
            {m.gain_setting && (
              <>
                <dt>Gain setting</dt>
                {/* The device's own word for it. Not shown as dB, because what
                    a step means depends on board revision and firmware — see
                    the calibration card for what that costs. */}
                <dd>{m.gain_setting}</dd>
              </>
            )}
            <dt>Position</dt>
            <dd className="num">
              {m.latitude != null && m.longitude != null
                ? `${m.latitude.toFixed(5)}, ${m.longitude.toFixed(5)}`
                : '—'}
            </dd>
            {/* What was arranged, beside what happened. Shown for a planned
                mission because it is the only thing on the card that says when
                it starts, and kept afterwards because a deployment planned for
                13:00 that opened at 14:07 should say so rather than read as
                punctual — the gap is usually the boat, and it is the first
                thing anyone asks about a window that looks odd. */}
            {m.activate_after && (
              <>
                <dt>{m.state === 'planned' ? 'Starts' : 'Was planned for'}</dt>
                <dd>
                  {fmtTime(m.activate_after)}
                  {m.state === 'planned' && (
                    <span className="hint" style={{ display: 'block' }}>
                      when the unit next makes contact after this
                    </span>
                  )}
                </dd>
              </>
            )}
            <dt>Ended</dt>
            <dd>{m.ended_at ? fmtTime(m.ended_at) : <span className="faint">still running</span>}</dd>
            <dt>Counts towards impact</dt>
            <dd>
              {m.purpose === 'test'
                ? <span className="badge">no — marked as a test</span>
                : 'yes — in the water'}
            </dd>
            <dt>Revision</dt>
            <dd className="num">{m.revision}</dd>
            {m.notes && (
              <>
                <dt>Notes</dt>
                <dd>{m.notes}</dd>
              </>
            )}
          </dl>

          {me.can_write && <Position m={m} onSaved={onSaved} />}

          {me.can_write && m.recorder_model && !m.ended_at && (
            <Recover missionId={m.mission_id} onSaved={onSaved} />
          )}
        </div>

        <CalibrationCard m={m} cal={cal} me={me} onSaved={onSaved} />
      </div>

      {/* What happens after this deployment, on the page for this deployment.
          It was only on the node page, which is the wrong place to look: a
          mission *is* one deployment episode, so the next one belongs next to
          the one it replaces. Shown only while this is the live deployment --
          on a closed mission it would invite arranging a move for a unit that
          has already moved. A recorder has no node and cannot be sent
          anywhere, so it has nothing to offer there either. */}
      {me.can_write && m.compute_id && m.state === 'active' && (
        <Move compute={m.compute_id} onSaved={onSaved} />
      )}
    </div>
  )
}

/** Where the unit actually went.
 *
 *  A deployment is arranged from a chart, and a chart is good to about a
 *  hundred metres. The mooring is known exactly once somebody has stood over
 *  it and read a handheld — and until now there was nowhere to put that. The
 *  alternative was re-commissioning, which mints a second mission and leaves
 *  the first as litter, and the mission is the location history, so littering
 *  it is the one thing not to do.
 *
 *  Correcting a position is not rewriting history. The mission is *one*
 *  deployment episode: the unit was always at the true position, and what is
 *  being fixed is the record's idea of where that was. Moving it somewhere
 *  else is a different deployment and goes through a redeploy.
 *
 *  If-Match on the revision, so two people correcting the same mooring from
 *  two boats cannot silently overwrite each other. A 412 says so in words —
 *  the raw code would read as a server fault when it is in fact the safety
 *  catch working.
 *
 *  The node learns the new position on its next poll without anyone writing
 *  desired state: its ETag is a hash of the response body, so a mission edit
 *  changes what it is served.
 */
function Position({ m, onSaved }: { m: M; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [lat, setLat] = useState('')
  const [lon, setLon] = useState('')
  const [depth, setDepth] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const start = () => {
    setLat(m.latitude != null ? String(m.latitude) : '')
    setLon(m.longitude != null ? String(m.longitude) : '')
    setDepth(m.deploy_depth_m != null ? String(m.deploy_depth_m) : '')
    setErr(null)
    setOpen(true)
  }

  // Parsed once so the map, the validity check and the save all read the same
  // numbers. An empty box is "leave it alone", not zero — zero is a real
  // coordinate in the Gulf of Guinea and a real depth at the surface.
  const nLat = lat.trim() === '' ? null : Number(lat)
  const nLon = lon.trim() === '' ? null : Number(lon)
  const nDepth = depth.trim() === '' ? null : Number(depth)
  const bad =
    (nLat !== null && (!Number.isFinite(nLat) || Math.abs(nLat) > 90)) ||
    (nLon !== null && (!Number.isFinite(nLon) || Math.abs(nLon) > 180)) ||
    (nDepth !== null && (!Number.isFinite(nDepth) || nDepth < 0))

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      const patch: Record<string, number> = {}
      if (nLat !== null) patch.latitude = nLat
      if (nLon !== null) patch.longitude = nLon
      if (nDepth !== null) patch.deploy_depth_m = nDepth
      // Emptying a box that had a value means "this was wrong", which is a
      // different request from "leave it alone" and needs the server's
      // explicit clear — otherwise a depth entered by mistake is
      // overwritable but never removable.
      const clear = [
        ...(nLat === null && m.latitude != null ? ['latitude'] : []),
        ...(nLon === null && m.longitude != null ? ['longitude'] : []),
        ...(nDepth === null && m.deploy_depth_m != null ? ['deploy_depth_m'] : []),
      ]
      if (!Object.keys(patch).length && !clear.length) { setOpen(false); return }
      await patchMission(m.mission_id, m.revision, patch, clear)
      onSaved()
      setOpen(false)
    } catch (e) {
      setErr(
        e instanceof ApiError && e.status === 412
          ? 'Someone else changed this deployment while you were editing. '
            + 'Reload to see their version, then apply your change on top.'
          : e instanceof ApiError ? e.detail ?? e.code
          : 'Could not save.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button className="btn ghost sm" style={{ marginTop: 14 }} onClick={start}>
        {m.latitude != null ? 'Adjust position' : 'Set position'}
      </button>
    )
  }

  return (
    <div className="stack" style={{ marginTop: 14, gap: 10 }}>
      {/* Dragging beats typing for the correction this exists for: the number
          from a handheld is copied once, but "a bit further out than the
          chart said" is a thing you point at. Both are offered, and they
          stay in step. */}
      <DeployMap
        pin={nLat !== null && nLon !== null && !bad ? { lat: nLat, lon: nLon } : null}
        onPick={(p) => { setLat(p.lat.toFixed(5)); setLon(p.lon.toFixed(5)) }}
      />
      <div className="row" style={{ gap: 10 }}>
        <Field label="Latitude">
          <input className="num" inputMode="decimal" value={lat}
            onChange={(e) => setLat(e.target.value)} placeholder="60.39299" />
        </Field>
        <Field label="Longitude">
          <input className="num" inputMode="decimal" value={lon}
            onChange={(e) => setLon(e.target.value)} placeholder="5.32415" />
        </Field>
        <Field label="Depth (m)">
          <input className="num" inputMode="decimal" value={depth}
            onChange={(e) => setDepth(e.target.value)} placeholder="15" />
        </Field>
      </div>
      {bad && (
        <div className="notice">
          Latitude is −90 to 90, longitude −180 to 180, depth zero or more.
        </div>
      )}
      {err && <div className="notice bad">{err}</div>}
      <div className="row">
        <button className="btn primary sm" onClick={() => void save()}
          disabled={busy || bad}>
          {busy ? 'Saving…' : 'Save position'}
        </button>
        <button className="btn ghost sm" onClick={() => setOpen(false)}
          disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/** Close a recorder's deployment on the day the card was actually pulled.
 *
 *  The fleet card's "Bring it in" stamps `now`, which is right for a node —
 *  its mission is closed by the successor that displaced it, so the moment of
 *  the click is the moment of the change. A recorder is different: it is
 *  pulled on Tuesday and sat on a desk until Friday, and stamping Friday
 *  widens the deployment window by three days. That window is what decides
 *  which of the card's recordings count as attributable, so the difference is
 *  not cosmetic — it is the difference between a clean import and a review
 *  queue full of false conflicts.
 */
function Recover({
  missionId, onSaved,
}: { missionId: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      // Local wall-clock in, instant out — the same conversion the
      // registration form makes, for the same reason.
      await transitionMission(missionId, 'recovered',
        at ? new Date(at).toISOString() : undefined)
      onSaved()
      setOpen(false)
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button className="btn ghost sm" style={{ marginTop: 14 }}
        onClick={() => setOpen(true)}>
        Mark recovered
      </button>
    )
  }

  return (
    <div className="stack" style={{ marginTop: 14, gap: 10 }}>
      <Field label="Recovered at" hint="When the unit came out of the water. Leave empty for now.">
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
      </Field>
      {err && <div className="notice bad">{err}</div>}
      <div className="row">
        <button className="btn primary sm" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Mark recovered'}
        </button>
        <button className="btn ghost sm" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/** The three terms of R2.3, and the only place they can be corrected.
 *
 *  Sensitivity is not editable here on purpose: it is versioned in
 *  mic_calibrations with validity dates (R2.4), so changing it is adding a
 *  dated certificate to the hydrophone, not overwriting a number on the
 *  mission. Editing it in place would silently rewrite what past recordings
 *  mean.
 */
function CalibrationCard({
  m, cal, me, onSaved,
}: { m: M; cal: Calibration; me: Me; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [gain, setGain] = useState(m.analogue_gain_db?.toString() ?? '')
  const [depth, setDepth] = useState(m.deploy_depth_m?.toString() ?? '')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      const body: Record<string, unknown> = {}
      if (gain !== '') body.analogue_gain_db = Number(gain)
      if (depth !== '') body.deploy_depth_m = Number(depth)
      // If-Match on the mission revision (R3.5): two people correcting the
      // same deployment from different boats must not silently overwrite each
      // other. A 412 here means someone else saved first.
      await api.patch(`/v1/missions/${encodeURIComponent(m.mission_id)}`, body, `"${m.revision}"`)
      setEditing(false)
      onSaved()
    } catch (e) {
      setErr(
        e instanceof ApiError && e.status === 412
          ? 'Someone else changed this mission while you were editing. Reload and try again.'
          : e instanceof ApiError ? e.detail ?? e.code : 'Save failed.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 className="display" style={{ fontSize: 17 }}>Calibration</h2>
        <CalibrationBadge cal={cal} />
      </div>

      <dl className="dl">
        <dt>Full scale</dt>
        <dd className="num">
          {cal.terms.full_scale_dbv != null
            ? `${cal.terms.full_scale_dbv} dBV`
            : <span className="faint">unknown — set it on the node</span>}
        </dd>
        <dt>Analogue gain</dt>
        <dd className="num">
          {cal.terms.analogue_gain_db != null
            ? `${cal.terms.analogue_gain_db} dB`
            : <span className="faint">unknown</span>}
        </dd>
        <dt>Sensitivity</dt>
        <dd className="num">
          {cal.terms.sensitivity_dbv_upa != null
            ? `${cal.terms.sensitivity_dbv_upa} dBV re 1 µPa`
            : <span className="faint">no dated certificate</span>}
        </dd>
        {cal.sensitivity_source && (
          <>
            <dt>Certificate</dt>
            <dd className="faint">
              {cal.sensitivity_source.certificate_ref ?? cal.sensitivity_source.method ?? '—'}
              {' · from '}{fmtTime(cal.sensitivity_source.valid_from)}
            </dd>
          </>
        )}
      </dl>

      <div className="notice" style={{ marginTop: 16 }}>
        {cal.absolute ? (
          <>Levels are <strong>{cal.basis}</strong> and comparable with other calibrated sites.</>
        ) : (
          <>
            Levels are <strong>{cal.basis}</strong>. Cross-site comparison is
            refused until {cal.missing.join(', ')} {cal.missing.length === 1 ? 'is' : 'are'} known.
          </>
        )}
      </div>

      {me.can_write && !editing && (
        <button className="btn sm" style={{ marginTop: 16 }} onClick={() => setEditing(true)}>
          Correct deployment
        </button>
      )}

      {editing && (
        <div className="stack" style={{ marginTop: 16, gap: 14 }}>
          <Field label="Analogue gain (dB)" hint="The preamp setting used for this deployment.">
            <input type="number" step="0.1" value={gain} onChange={(e) => setGain(e.target.value)} />
          </Field>
          <Field label="Deploy depth (m)">
            <input type="number" step="0.1" value={depth} onChange={(e) => setDepth(e.target.value)} />
          </Field>
          {err && <div className="notice bad">{err}</div>}
          <div className="row">
            {/* Save outranks Cancel by being a solid button next to a ghost
                one, which is hierarchy enough. The accent stays with the
                page's one call to action. */}
            <button className="btn sm" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------- recordings */

const KINDS = ['', 'audio', 'clips', 'reference', 'events', 'features', 'meta', 'live']

/** Clips for one day, with what makes each one interpretable.
 *
 *  The capture gain is on every row, not hidden behind the player. Two clips
 *  are only comparable if you can see what each was boosted by, and rule 5 --
 *  uncalibrated means relative -- makes that the difference between a number
 *  and a measurement.
 */
function DayClips({
  missionId, day, onOpen,
}: {
  missionId: string
  day: string
  onOpen: (key: string) => void
}) {
  // One request for the whole day: the timeline's hours and the clip rows come
  // back together, so opening a day is a single round trip rather than one per
  // component and one per row.
  const q = useApi<{
    hours: DayHour[]; clips: ClipRow[]; uploads_audio: boolean
    node_observed_utc: string | null
    events: DetectedEvent[]; calibration: Calibration
    event_minutes: number; observed_minutes: number
  }>(`/v1/missions/${encodeURIComponent(missionId)}/day${qs({ date: day })}`)

  if (q.cold) return <Rows n={2} />
  const items = q.data?.clips ?? []

  const timeline = (
    <DayTimeline
      date={day}
      hours={q.data?.hours ?? []}
      clips={items}
      uploadsAudio={!!q.data?.uploads_audio}
      observedUtc={q.data?.node_observed_utc ?? null}
      /* A mark has no object, so there is nothing for the object player to
         open. Its audio is the day's own segments and it is played on the
         customer's Listen page, which is the only screen that knows how. */
      onOpenClip={(c) => { if (c.key) onOpen(c.key) }}
    />
  )

  const detected = (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ fontSize: 14, marginBottom: 8 }}>
        What the node noticed{' '}
        <span className="faint num">({q.data?.events?.length ?? 0})</span>
      </h3>
      <p className="faint" style={{ fontSize: 12, margin: '0 2px 10px' }}>
        The detector's own marks, grouped into episodes. There is no audio
        behind these unless a recording above covers the same moment — the node
        keeps its audio on the card.
      </p>
      <EventList
        events={q.data?.events ?? []}
        calibration={q.data?.calibration ?? { basis: 'dBFS', absolute: false } as Calibration}
        eventMinutes={q.data?.event_minutes ?? 0}
        observedMinutes={q.data?.observed_minutes ?? 0}
      />
    </div>
  )

  const heading = (
    <h3 style={{ fontSize: 14, margin: '18px 0 8px' }}>
      Recordings you can listen to{' '}
      <span className="faint num">({items.length})</span>
    </h3>
  )

  if (!items.length) {
    return (
      <>
        {timeline}
        {heading}
        <p className="faint" style={{ margin: '4px 2px 4px' }}>
          Nothing saved on {day}. Recordings are made from the Listen tab: press
          Record while listening and the clip is saved here and to your device.
        </p>
        {detected}
      </>
    )
  }
  return (
    <>
      {timeline}
      {heading}
    <div className="scroll-x">
      <table className="tbl">
        <thead>
          <tr>
            <th>Started</th>
            <th className="r">Length</th>
            <th className="r">Capture gain</th>
            <th>Note</th>
            <th>Saved by</th>
            <th className="r">Size</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.clip_id}>
              <td data-l="Started">
                {c.key ? (
                  <button
                    className="rowlink"
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
                    onClick={() => onOpen(c.key!)}
                  >
                    {fmtTime(c.started_utc)}
                  </button>
                ) : (
                  /* Not a link, because there is nothing here to open. Saying
                     "merke" beside it is what stops that reading as a broken
                     row: the clip is real and plays on the farm's Listen page,
                     it just is not an object in this bucket. */
                  <>
                    {fmtTime(c.started_utc)}{' '}
                    <span className="faint">merke</span>
                  </>
                )}
              </td>
              <td data-l="Length" className="r num">{c.duration_s.toFixed(1)} s</td>
              <td data-l="Capture gain" className="r num">
                {/* Null on a mark, and an em dash rather than 0.0 dB: nobody
                    captured anything, and printing a figure would state a
                    measurement that was never made. */}
                {c.gain_db == null ? '—'
                  : `${c.gain_db >= 0 ? '+' : '−'}${Math.abs(c.gain_db).toFixed(1)} dB`}
              </td>
              <td data-l="Note" className="faint">{c.note || ''}</td>
              <td data-l="Saved by" className="faint">{c.created_by_name || c.created_by}</td>
              <td data-l="Size" className="r num">{c.key ? fmtBytes(c.size) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  )
}

interface ClipRow {
  clip_id: string
  /* Null on a mark: a span of the archive somebody kept, which points at
     recordings already in the bucket rather than being a file of its own.
     There is nothing for this page to open, and nothing to weigh. */
  key: string | null
  started_utc: string
  duration_s: number
  gain_db: number | null
  created_by: string
  created_by_name: string | null
  size: number | null
  source?: 'live' | 'archive'
  note?: string | null
}

function thisMonth(): string {
  const d = new Date()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

function Recordings({
  missionId, calibration, recorderModel, recorderSerial,
}: {
  missionId: string
  calibration: Calibration
  /** Non-null means this deployment is a self-recording unit, whose audio
   *  arrives only by someone carrying the card back. */
  recorderModel?: string | null
  recorderSerial?: string | null
}) {
  const [month, setMonth] = useState(thisMonth)
  const [day, setDay] = useState<string | null>(null)
  const cal = useApi<{ days: CalendarDay[]; uploads_audio: boolean }>(
    `/v1/missions/${encodeURIComponent(missionId)}/calendar${qs({ month })}`)

  /* Opening a clip from the day list.
   *
   * The clip list returns keys; ClipPlayer takes the object row the flat list
   * hands it. Rather than duplicate that shape, the key is looked up through
   * the same objects endpoint the list already uses -- one request, and the
   * player then behaves identically however it was reached. */
  const openByKey = useCallback(async (key: string) => {
    try {
      const r = await api.get<{ key: string; kind: string; size: number | null; start_utc: string }>(
        `/v1/objects/url${qs({ key })}`)
      setOpen({ key: r.key, kind: r.kind, size: r.size, ts: r.start_utc,
                state: 'stored', attribution_conflict: false } as StoredObject)
    } catch {
      /* The player surfaces its own failure; a second message here would be
         two errors for one problem. */
    }
  }, [])

  const [kind, setKind] = useState('')
  const [rows, setRows] = useState<StoredObject[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [more, setMore] = useState<string | null>(null)
  const [open, setOpen] = useState<StoredObject | null>(null)

  // Whether the import panel is expanded.
  //
  // Held here rather than derived from `rows.length`, which is what it used to
  // be. `open` on a <details> is a controlled attribute: React rewrites it on
  // every render, so any render where the expression turned false slammed the
  // panel shut. And an import makes it turn false the moment the first file
  // lands — the panel reloads the object list, the list stops being empty, and
  // the operator watching their card upload had it collapse in front of them.
  //
  // So the emptiness of the mission decides this exactly once, when the first
  // page of objects settles, and after that it is the operator's to open and
  // close. Nothing that happens during an import can close it.
  const [importOpen, setImportOpen] = useState(true)
  const importDecided = useRef(false)

  const base = `/v1/missions/${encodeURIComponent(missionId)}/objects`
  const q = useApi<{ items: StoredObject[]; next_cursor: string | null }>(
    `${base}${qs({ kind, cursor, limit: 60 })}`,
  )

  // Reset the accumulated page when the filter changes, or the new kind's rows
  // append to the old kind's.
  useEffect(() => {
    setRows([])
    setCursor(null)
    setOpen(null)
  }, [kind])

  useEffect(() => {
    if (!q.data) return
    setRows((prev) => (cursor ? [...prev, ...q.data!.items] : q.data!.items))
    setMore(q.data.next_cursor)
    if (!importDecided.current) {
      importDecided.current = true
      setImportOpen(q.data.items.length === 0)
    }
  }, [q.data, cursor])

  return (
    <div className="stack">
      {open && (
        <ClipPlayer object={open} calibration={calibration} onClose={() => setOpen(null)} />
      )}

      {/* Only for hardware that records to a card. A node's audio arrives over
          its uplink and an import panel on that page would be an invitation to
          upload a copy of what is already here.

          First on the page, because on a recorder's mission everything below
          is empty until a card has been imported -- and `/v1/commission/recorder`
          ends by telling the operator to come to this tab and do exactly this. */}
      {recorderModel && (
        <details
          className="card flush"
          open={importOpen}
          onToggle={(e) => setImportOpen(e.currentTarget.open)}
        >
          <summary className="card-head"><h2>Import card</h2></summary>
          <div className="card-body">
            <p className="lead" style={{ marginTop: 0 }}>
              {recorderModel} records to its own card with no uplink. Drop the
              card's folders here to bring its recordings in.
            </p>
            <CardImport
              missionId={missionId}
              recorderSerial={recorderSerial}
              onImported={() => {
                // The calendar and the object list are both now wrong.
                invalidate(`/v1/missions/${encodeURIComponent(missionId)}/calendar`)
                cal.reload()
                setRows([]); setCursor(null)
                q.reload()
              }}
            />
          </div>
        </details>
      )}

      {/* Two ways in, and the calendar is first because it answers the question
          people actually arrive with -- "I know roughly when it happened".
          The flat object list answers "what is the most recent thing", which is
          a different and rarer question, so it moves below rather than away. */}
      <div className="card flush">
        <div className="card-head"><h2>By day</h2></div>
        {/* Side by side once there is room: picking a day and seeing what is
            in it are one task, and making the day view a scroll away turned a
            glance into a journey. Stacked on a phone, calendar first, because
            it is what decides the rest of the panel. */}
        <div className="card-body day-split">
          <Calendar
            month={month}
            days={cal.data?.days ?? []}
            selected={day}
            uploadsAudio={!!cal.data?.uploads_audio}
            onMonth={(m) => { setMonth(m); setDay(null) }}
            onSelect={(d) => setDay(d === day ? null : d)}
          />
          <div>
            {day ? (
              <DayClips missionId={missionId} day={day} onOpen={openByKey} />
            ) : (
              <p className="faint" style={{ margin: 0 }}>
                Pick a day to see what was recorded and what was saved.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="seg">
          {KINDS.map((k) => (
            <button key={k || 'all'} aria-selected={k === kind} onClick={() => setKind(k)}>
              {k || 'all'}
            </button>
          ))}
        </div>
      </div>

      <div className="card flush">
        {q.cold ? (
          <Rows />
        ) : rows.length === 0 ? (
          <Empty title="Nothing here yet">
            No objects of this kind have been indexed for this mission. Which
            kinds a node may send is governed by its tier.
          </Empty>
        ) : (
          <div className="scroll-x">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Object</th>
                  <th>Kind</th>
                  <th>Recorded</th>
                  <th className="r">Size</th>
                  <th className="r">State</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.key}>
                    <td data-l="Object">
                      <button
                        className="rowlink"
                        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        onClick={() => setOpen(o)}
                      >
                        {o.key.split('/').pop()}
                      </button>
                      {o.attribution_conflict && (
                        <span className="badge alert" style={{ marginLeft: 8 }}>
                          attribution conflict
                        </span>
                      )}
                    </td>
                    <td data-l="Kind" className="faint">{o.kind}</td>
                    <td data-l="Recorded" className="num faint">{fmtTime(o.ts)}</td>
                    <td data-l="Size" className="r num">{fmtBytes(o.size)}</td>
                    <td data-l="State" className="r">
                      <span className={`badge${o.state === 'stored' ? ' on' : ' off'}`}>{o.state}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {more && (
        <button className="btn" disabled={q.loading} onClick={() => setCursor(more)}>
          {q.loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- activity */

function Activity({ missionId, me }: { missionId: string; me: Me }) {
  const q = useApi<Arrivals>(`/v1/missions/${encodeURIComponent(missionId)}/arrivals?days=30`)
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const a = q.data

  const act = async (what: 'attribution:resolve' | 'objects:reconcile') => {
    setBusy(what); setNote(null)
    try {
      const r = await api.post<Record<string, number>>(
        `/v1/missions/${encodeURIComponent(missionId)}/${what}`)
      setNote(what === 'attribution:resolve'
        ? `Accepted ${r.resolved ?? 0} object${r.resolved === 1 ? '' : 's'} onto this mission and widened its window to match.`
        : `Checked ${r.checked ?? 0}; ${r.marked_missing ?? 0} are no longer in storage, ${r.restored ?? 0} came back.`)
      invalidate(`/v1/missions/${encodeURIComponent(missionId)}`)
      q.reload()
    } catch (e) {
      setNote(e instanceof ApiError ? e.detail ?? e.code : 'Failed.')
    } finally {
      setBusy('')
    }
  }

  if (q.cold) return <Rows n={6} />
  if (!a) return <ErrorBox error={q.error} />

  const days = [...new Set(a.by_day.map((d) => d.day))].sort()
  const kinds = [...new Set(a.by_day.map((d) => d.kind))].sort()
  const max = Math.max(1, ...a.by_day.map((d) => d.count))

  return (
    <div className="stack">
      <div className="grid c3">
        {a.by_kind.map((k) => (
          <div className="card" key={k.kind}>
            <Stat label={k.kind} value={k.count.toLocaleString()} small />
            <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
              {fmtBytes(k.bytes)} · newest {fmtTime(k.latest)}
            </div>
          </div>
        ))}
      </div>

      {note && <div className="notice">{note}</div>}

      {a.attribution_conflicts > 0 && (
        <div className="notice bad">
          <div style={{ marginBottom: 12 }}>
            {a.attribution_conflicts} object
            {a.attribution_conflicts === 1 ? '' : 's'} were recorded outside this
            mission's start and end times, so they were stored and flagged rather
            than accepted silently. Usually that just means the unit was already
            listening before the mission was written down.
          </div>
          {me.can_write && (
            <button className="btn sm" disabled={busy !== ''}
              onClick={() => void act('attribution:resolve')}>
              {busy === 'attribution:resolve' ? 'Working…' : 'These are from this mission'}
            </button>
          )}
        </div>
      )}

      <div className="card flush">
        <div className="card-head"><h2>Arrivals, last 30 days</h2></div>
        <div className="card-body">
          {days.length === 0 ? (
            <Empty title="No arrivals in this window" />
          ) : (
            <div className="scroll-x">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Day</th>
                    {kinds.map((k) => <th key={k} className="r">{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d}>
                      <td className="num faint">
                        {new Date(d).toLocaleDateString(undefined, { month: 'short', day: '2-digit' })}
                      </td>
                      {kinds.map((k) => {
                        const cell = a.by_day.find((x) => x.day === d && x.kind === k)
                        const n = cell?.count ?? 0
                        return (
                          <td key={k} className="r num">
                            {n > 0 ? (
                              <span
                                title={`${n} objects, ${fmtBytes(cell!.bytes)}`}
                                style={{
                                  display: 'inline-block', minWidth: 34, padding: '2px 7px',
                                  borderRadius: 5,
                                  // Density as opacity, in Signal. A bar chart
                                  // would imply a scale that a count per kind
                                  // does not have.
                                  background: `rgba(180,217,199,${0.08 + 0.34 * (n / max)})`,
                                }}
                              >
                                {n}
                              </span>
                            ) : (
                              <span className="faint">·</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {me.can_write && (
        <div className="row">
          <button className="btn sm" disabled={busy !== ''}
            onClick={() => void act('objects:reconcile')}>
            {busy === 'objects:reconcile' ? 'Checking…' : 'Check storage'}
          </button>
          <span className="faint" style={{ fontSize: 13 }}>
            Confirms every indexed object is really in the bucket, and marks the
            ones that are not.
          </span>
        </div>
      )}

      <p className="faint" style={{ fontSize: 13, maxWidth: '74ch' }}>
        This counts what arrived. What it sounded like is on the Sound tab,
        drawn from the level roll-ups the node uploads.
      </p>
    </div>
  )
}
